/**
 * Article Enhanced JavaScript
 *
 * Handles table enhancements for enhanced blog articles:
 * - Auto-generates data-label attributes for responsive card layout
 * - Manages horizontal scroll indicators
 * - Improves keyboard navigation
 *
 * @version 1.0.0
 * @created 2026-02-04
 * @author YLI Development Team
 */

(function() {
  'use strict';

  /**
   * Initialize all table enhancements when DOM is ready
   */
  function initTableEnhancements() {
    const tables = document.querySelectorAll('.comparison-table');

    tables.forEach(table => {
      generateDataLabels(table);
      setupScrollIndicators(table);
      improveKeyboardNav(table);
    });
  }

  /**
   * Auto-generate data-label attributes from table headers
   * Enables mobile card layout without manual attribute management
   *
   * @param {HTMLTableElement} table - The table element to enhance
   */
  function generateDataLabels(table) {
    const headers = table.querySelectorAll('thead th');
    const headerTexts = Array.from(headers).map(th => th.textContent.trim());

    // Skip if no headers found
    if (headerTexts.length === 0) return;

    const rows = table.querySelectorAll('tbody tr');

    rows.forEach(row => {
      const cells = row.querySelectorAll('td');

      cells.forEach((cell, index) => {
        // Only add if data-label doesn't already exist
        if (!cell.hasAttribute('data-label') && headerTexts[index]) {
          cell.setAttribute('data-label', headerTexts[index]);
        }
      });
    });
  }

  /**
   * Setup horizontal scroll indicators for table wrapper
   * Shows gradient overlays when table can scroll left/right
   *
   * @param {HTMLTableElement} table - The table element
   */
  function setupScrollIndicators(table) {
    const wrapper = table.closest('.table-responsive');
    if (!wrapper) return;

    // Update scroll indicators based on scroll position
    function updateScrollIndicators() {
      const scrollLeft = wrapper.scrollLeft;
      const scrollWidth = wrapper.scrollWidth;
      const clientWidth = wrapper.clientWidth;
      const maxScroll = scrollWidth - clientWidth;

      // Determine scroll state
      if (maxScroll <= 1) {
        // No scroll needed
        wrapper.removeAttribute('data-scroll');
      } else if (scrollLeft <= 1) {
        // At left edge, can scroll right
        wrapper.setAttribute('data-scroll', 'right');
      } else if (scrollLeft >= maxScroll - 1) {
        // At right edge, can scroll left
        wrapper.setAttribute('data-scroll', 'left');
      } else {
        // In middle, can scroll both ways
        wrapper.setAttribute('data-scroll', 'both');
      }
    }

    // Update on scroll
    wrapper.addEventListener('scroll', updateScrollIndicators);

    // Update on resize
    window.addEventListener('resize', updateScrollIndicators);

    // Initial update
    updateScrollIndicators();
  }

  /**
   * Improve keyboard navigation for tables
   * Makes table cells focusable and adds arrow key navigation
   *
   * @param {HTMLTableElement} table - The table element
   */
  function improveKeyboardNav(table) {
    const cells = table.querySelectorAll('th, td');

    // Make cells focusable
    cells.forEach(cell => {
      if (!cell.hasAttribute('tabindex')) {
        cell.setAttribute('tabindex', '-1');
      }
    });

    // Arrow key navigation
    table.addEventListener('keydown', function(e) {
      const target = e.target;

      // Only handle if focus is on a cell
      if (!target.matches('th, td')) return;

      const row = target.parentElement;
      const rowIndex = Array.from(row.parentElement.children).indexOf(row);
      const cellIndex = Array.from(row.children).indexOf(target);

      let newTarget = null;

      switch(e.key) {
        case 'ArrowRight':
          // Move to next cell in row
          if (target.nextElementSibling) {
            newTarget = target.nextElementSibling;
          }
          break;

        case 'ArrowLeft':
          // Move to previous cell in row
          if (target.previousElementSibling) {
            newTarget = target.previousElementSibling;
          }
          break;

        case 'ArrowDown':
          // Move to cell below
          const nextRow = row.nextElementSibling;
          if (nextRow) {
            newTarget = nextRow.children[cellIndex];
          }
          break;

        case 'ArrowUp':
          // Move to cell above
          const prevRow = row.previousElementSibling;
          if (prevRow) {
            newTarget = prevRow.children[cellIndex];
          } else if (row.parentElement.tagName === 'TBODY') {
            // Move to header row if at top of body
            const thead = table.querySelector('thead');
            if (thead) {
              const headerRow = thead.querySelector('tr');
              if (headerRow) {
                newTarget = headerRow.children[cellIndex];
              }
            }
          }
          break;

        case 'Home':
          // Move to first cell in row
          newTarget = row.children[0];
          break;

        case 'End':
          // Move to last cell in row
          newTarget = row.children[row.children.length - 1];
          break;
      }

      if (newTarget) {
        e.preventDefault();
        newTarget.focus();
      }
    });

    // Click to focus (for screen readers)
    cells.forEach(cell => {
      cell.addEventListener('click', function() {
        this.focus();
      });
    });
  }

  /**
   * Initialize when DOM is ready
   */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTableEnhancements);
  } else {
    initTableEnhancements();
  }

})();
